import React from "react";

import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";

import styles from "./VoterApplicationListFacets.module.styl";

const EXCLUSION_TAGS = Object.freeze({
  exclude: {
    label: t("filter.exclude"),
    aliases: ["exclude", "excluded", "ignore"],
  },
});

const EXCLUSION_TAG_STATES = Object.freeze([
  { value: "exclude", label: t("filter.hide") },
  { value: "include", label: t("filter.include") },
  { value: "only", label: t("filter.only") },
]);

const DEFAULT_EXCLUSION_TAG_FILTERS = Object.freeze({
  exclude: "exclude",
});

const DEFAULT_STATUS_FILTERS = Object.freeze([1, 2, 3, 4, 6, 7, 8, 9]);

export const DEFAULT_VOTER_APPLICATION_LIST_FILTERS = Object.freeze({
  gender: [],
  minAge: null,
  maxAge: null,
  status: DEFAULT_STATUS_FILTERS,
  applicationType: [],
  exclusionTags: DEFAULT_EXCLUSION_TAG_FILTERS,
});

const GENDER_OPTIONS = [
  { value: "male", label: t("filter.male") },
  { value: "female", label: t("filter.female") },
  { value: "unknown", label: t("common.unknown") },
];

const STATUS_OPTIONS = [
  { value: 1, label: t("common.submitted") },
  { value: 2, label: t("common.error") },
  { value: 3, label: t("common.approved") },
  { value: 4, label: t("voter.manualAdjudication") },
  { value: 5, label: t("voter.retransmission") },
  { value: 6, label: t("voter.duplicate") },
  { value: 7, label: t("common.rejected") },
  { value: 8, label: t("common.archived") },
  { value: 9, label: t("voter.checked") },
];

const APPLICATION_TYPE_OPTIONS = [
  { value: 1, label: t("voter.standard") },
  { value: 2, label: t("voter.correction") },
];

const normalizeArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => String(item || "").trim()).filter(Boolean);
};

const normalizeNumberArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item));
};

const normalizeChoice = (value, allowed, fallback) => {
  return allowed.includes(value) ? value : fallback;
};

const normalizeAge = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const number = Number(value);
  if (!Number.isFinite(number)) {
    return null;
  }

  return Math.max(0, Math.min(150, Math.floor(number)));
};

const normalizeExclusionTagFilters = (value) => {
  const input = value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};

  return {
    exclude: normalizeChoice(
      input.exclude,
      ["exclude", "include", "only"],
      DEFAULT_EXCLUSION_TAG_FILTERS.exclude
    ),
  };
};

export const normalizeVoterApplicationListFilters = (value) => {
  const input = value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};

  return {
    gender: normalizeArray(input.gender).filter((item) =>
      ["male", "female", "unknown"].includes(item)
    ),
    minAge: normalizeAge(input.minAge),
    maxAge: normalizeAge(input.maxAge),
    status: normalizeNumberArray(
      input.status === undefined ? DEFAULT_STATUS_FILTERS : input.status
    ).filter((item) => STATUS_OPTIONS.some((option) => option.value === item)),
    applicationType: normalizeNumberArray(input.applicationType).filter((item) =>
      [1, 2].includes(item)
    ),
    exclusionTags: normalizeExclusionTagFilters(input.exclusionTags),
  };
};

export const isDefaultVoterApplicationListFilters = (value) => {
  const filters = normalizeVoterApplicationListFilters(value);

  return filters.gender.length === 0 &&
    filters.minAge === null &&
    filters.maxAge === null &&
    filters.status.length === DEFAULT_STATUS_FILTERS.length &&
    DEFAULT_STATUS_FILTERS.every((status) => filters.status.includes(status)) &&
    filters.applicationType.length === 0 &&
    filters.exclusionTags.exclude === DEFAULT_EXCLUSION_TAG_FILTERS.exclude;
};

export const getVoterApplicationListActiveFilterCount = (value) => {
  const filters = normalizeVoterApplicationListFilters(value);
  let count = 0;

  count += filters.gender.length;

  if (filters.minAge !== null || filters.maxAge !== null) {
    count += 1;
  }

  const statusIsDefault =
    filters.status.length === DEFAULT_STATUS_FILTERS.length &&
    DEFAULT_STATUS_FILTERS.every((status) => filters.status.includes(status));

  if (!statusIsDefault) {
    count += 1;
  }

  count += filters.applicationType.length;

  if (filters.exclusionTags.exclude !== DEFAULT_EXCLUSION_TAG_FILTERS.exclude) {
    count += 1;
  }

  return count;
};

export const buildVoterApplicationListPayload = ({ search, filters, limit, offset }) => ({
  search: String(search || "").trim(),
  filters: normalizeVoterApplicationListFilters(filters),
  limit,
  offset,
});

export const loadVoterApplicationFacetCounts = async ({ search, filters }) => {
  return await rest.request(
    getServiceUri() + "voter-application-list/facets",
    "POST",
    buildVoterApplicationListPayload({
      search,
      filters,
      limit: null,
      offset: null,
    })
  );
};

const countFor = (counts, path, fallback = 0) => {
  let current = counts || {};

  for (const key of path) {
    if (!current || typeof current !== "object") {
      return fallback;
    }

    current = current[key];
  }

  const number = Number(current);
  return Number.isFinite(number) ? number : fallback;
};

const statusCount = (counts, value) => {
  const rows = Array.isArray(counts?.status) ? counts.status : [];
  const found = rows.find((item) => Number(item.value) === Number(value));
  return Number(found?.count || 0);
};

const applicationTypeCount = (counts, value) => {
  const rows = Array.isArray(counts?.applicationType)
    ? counts.applicationType
    : [];
  const found = rows.find((item) => Number(item.value) === Number(value));
  return Number(found?.count || 0);
};

const toggleArrayValue = (values, value) => {
  const current = normalizeArray(values);

  return current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
};

const toggleNumberArrayValue = (values, value) => {
  const current = normalizeNumberArray(values);

  return current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
};

const FacetSection = ({ title, children }) => (
  <section className={styles.section}>
    <div className={styles.sectionTitle}>{title}</div>
    <div className={styles.sectionBody}>{children}</div>
  </section>
);

const CheckboxOption = ({ label, selected, count, onClick }) => (
  <button
    type="button"
    className={selected ? `${styles.option} ${styles.optionSelected}` : styles.option}
    onClick={onClick}
  >
    <span className={styles.checkBox}>{selected ? "✓" : ""}</span>
    <span className={styles.optionLabel}>{label}</span>
    {count !== undefined ? <span className={styles.count}>{count}</span> : null}
  </button>
);

const Chip = ({ label, onRemove }) => (
  <button type="button" className={styles.chip} onClick={onRemove}>
    <span>{label}</span>
    <span className={styles.chipClose}>×</span>
  </button>
);

const setExclusionTagState = (filters, state) => ({
  exclusionTags: {
    ...filters.exclusionTags,
    exclude: state,
  },
});

export const VoterApplicationListFacetButton = ({ controller }) => {
  const activeCount = controller?.activeCount || 0;

  return (
    <button
      type="button"
      className={activeCount ? `${styles.filterButton} ${styles.filterButtonActive}` : styles.filterButton}
      onClick={controller.toggleDrawer}
    >
      <span className={styles.filterButtonIcon}>
        <TuneIcon fontSize="inherit" />
      </span>
      <span className={styles.filterButtonLabel}>{t("common.filters")}</span>
      {activeCount ? <span className={styles.filterButtonBadge}>{activeCount}</span> : null}
    </button>
  );
};

export const VoterApplicationListFacetChips = ({ controller }) => {
  const filters = normalizeVoterApplicationListFilters(controller?.value);

  if (isDefaultVoterApplicationListFilters(filters)) {
    return null;
  }

  const set = (patch) => controller.patchValue(patch);

  return (
    <div className={styles.chipsBar}>
      {filters.gender.map((value) => (
        <Chip
          key={`gender-${value}`}
          label={`${t("filter.gender")}: ${GENDER_OPTIONS.find((item) => item.value === value)?.label || value}`}
          onRemove={() => set({ gender: filters.gender.filter((item) => item !== value) })}
        />
      ))}

      {filters.minAge !== null ? (
        <Chip
          label={`${t("filter.minimumAge")}: ${filters.minAge}`}
          onRemove={() => set({ minAge: null })}
        />
      ) : null}

      {filters.maxAge !== null ? (
        <Chip
          label={`${t("filter.maximumAge")}: ${filters.maxAge}`}
          onRemove={() => set({ maxAge: null })}
        />
      ) : null}

      {(
        filters.status.length === DEFAULT_STATUS_FILTERS.length &&
        DEFAULT_STATUS_FILTERS.every((status) => filters.status.includes(status))
          ? []
          : filters.status
      ).map((value) => (
        <Chip
          key={`status-${value}`}
          label={`${t("common.status")}: ${STATUS_OPTIONS.find((item) => item.value === value)?.label || value}`}
          onRemove={() => set({ status: filters.status.filter((item) => item !== value) })}
        />
      ))}

      {filters.applicationType.map((value) => (
        <Chip
          key={`application-type-${value}`}
          label={`${t("voter.applicationType")}: ${APPLICATION_TYPE_OPTIONS.find((item) => item.value === value)?.label || value}`}
          onRemove={() => set({ applicationType: filters.applicationType.filter((item) => item !== value) })}
        />
      ))}

      {filters.exclusionTags.exclude !== DEFAULT_EXCLUSION_TAG_FILTERS.exclude ? (
        <Chip
          label={`${t("filter.exclude")}: ${EXCLUSION_TAG_STATES.find((item) => item.value === filters.exclusionTags.exclude)?.label || filters.exclusionTags.exclude}`}
          onRemove={() => set(setExclusionTagState(filters, DEFAULT_EXCLUSION_TAG_FILTERS.exclude))}
        />
      ) : null}

      <button type="button" className={styles.clearAll} onClick={controller.reset}>
        {t("common.clearAll")}
      </button>
    </div>
  );
};

const AgeRangeFilter = ({ filters, onSet }) => (
  <div className={styles.ageRange}>
    <label className={styles.ageField}>
      <span className={styles.ageFieldLabel}>{t("filter.minimumAge")}</span>
      <input
        type="number"
        min="0"
        max="150"
        step="1"
        className={styles.ageInput}
        value={filters.minAge ?? ""}
        onChange={(event) => onSet({ minAge: normalizeAge(event.target.value) })}
      />
    </label>

    <label className={styles.ageField}>
      <span className={styles.ageFieldLabel}>{t("filter.maximumAge")}</span>
      <input
        type="number"
        min="0"
        max="150"
        step="1"
        className={styles.ageInput}
        value={filters.maxAge ?? ""}
        onChange={(event) => onSet({ maxAge: normalizeAge(event.target.value) })}
      />
    </label>
  </div>
);

const ExclusionsSection = ({ filters, counts, onSet }) => {
  const [open, setOpen] = React.useState(false);
  const state = filters.exclusionTags.exclude;
  const changed = state !== DEFAULT_EXCLUSION_TAG_FILTERS.exclude;

  return (
    <section className={styles.exclusionCollapsedSection}>
      <button
        type="button"
        className={styles.exclusionCollapseButton}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.exclusionCollapseText}>{t("filter.exclusions")}</span>
        {changed ? <span className={styles.exclusionChangedBadge}>1</span> : null}
        <span className={open ? `${styles.exclusionChevron} ${styles.exclusionChevronOpen}` : styles.exclusionChevron}>
          <ExpandMoreIcon fontSize="inherit" />
        </span>
      </button>

      {open ? (
        <div className={styles.exclusionCollapsedBody}>
          <div className={styles.exclusionCompactHelp}>
            {t("filter.exclusionHelp")}
          </div>

          <div className={styles.exclusionCompactRow}>
            <div className={styles.exclusionCompactMeta}>
              <span className={styles.exclusionCompactName}>{t("filter.exclude")}</span>
              <span className={styles.exclusionCompactCount}>{countFor(counts, ["exclusions", "exclude"])}</span>
            </div>

            <div className={styles.segmentedControl}>
              {EXCLUSION_TAG_STATES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={state === option.value ? `${styles.segmentedButton} ${styles.segmentedButtonSelected}` : styles.segmentedButton}
                  onClick={() => onSet(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

const VoterApplicationListFacets = ({ controller }) => {
  const filters = normalizeVoterApplicationListFilters(controller?.value);
  const counts = controller?.counts || {};
  const set = (patch) => controller.patchValue(patch);

  return (
    <div className={styles.drawer}>
      <div className={styles.drawerHeader}>
        <div>
          <div className={styles.drawerTitle}>{t("common.filters")}</div>
          <div className={styles.drawerSubtitle}>
            {controller.countsLoading ? t("common.updatingCounts") : t("filter.refineApplications")}
          </div>
        </div>

        <button type="button" className={styles.closeButton} onClick={controller.closeDrawer} aria-label={t("common.closeFilters")}>
          <CloseIcon fontSize="inherit" />
        </button>
      </div>

      <div className={styles.drawerBody}>
        <FacetSection title={t("common.status")}>
          {STATUS_OPTIONS.map((option) => (
            <CheckboxOption
              key={option.value}
              label={option.label}
              selected={filters.status.includes(option.value)}
              count={statusCount(counts, option.value)}
              onClick={() => set({ status: toggleNumberArrayValue(filters.status, option.value) })}
            />
          ))}
        </FacetSection>

        <FacetSection title={t("filter.gender")}>
          {GENDER_OPTIONS.map((option) => (
            <CheckboxOption
              key={option.value}
              label={option.label}
              selected={filters.gender.includes(option.value)}
              count={countFor(counts, ["gender", option.value])}
              onClick={() => set({ gender: toggleArrayValue(filters.gender, option.value) })}
            />
          ))}
        </FacetSection>

        <FacetSection title={t("filter.age")}>
          <AgeRangeFilter filters={filters} onSet={set} />
        </FacetSection>

        <FacetSection title={t("voter.applicationType")}>
          {APPLICATION_TYPE_OPTIONS.map((option) => (
            <CheckboxOption
              key={option.value}
              label={option.label}
              selected={filters.applicationType.includes(option.value)}
              count={applicationTypeCount(counts, option.value)}
              onClick={() => set({ applicationType: toggleNumberArrayValue(filters.applicationType, option.value) })}
            />
          ))}
        </FacetSection>

        <ExclusionsSection
          filters={filters}
          counts={counts}
          onSet={(state) => set(setExclusionTagState(filters, state))}
        />
      </div>

      <div className={styles.drawerFooter}>
        <button
          type="button"
          className={styles.secondaryAction}
          onClick={controller.reset}
          disabled={controller.activeCount === 0}
        >
          <RestartAltIcon fontSize="inherit" />
          <span>{t("common.resetFilters")}</span>
        </button>

        <button type="button" className={styles.primaryAction} onClick={controller.closeDrawer}>
          {t("common.done")}
        </button>
      </div>
    </div>
  );
};

export const VOTER_APPLICATION_LIST_FACETS = {
  storageKey: "voter-application.v3",
  overlayLabel: t("filter.applicationFilters"),
  defaultValue: DEFAULT_VOTER_APPLICATION_LIST_FILTERS,
  normalizeValue: normalizeVoterApplicationListFilters,
  isDefaultValue: isDefaultVoterApplicationListFilters,
  getActiveCount: getVoterApplicationListActiveFilterCount,
  loadCounts: loadVoterApplicationFacetCounts,
  renderButton: (controller) => <VoterApplicationListFacetButton controller={controller} />,
  renderDrawer: (controller) => <VoterApplicationListFacets controller={controller} />,
  renderChips: (controller) => <VoterApplicationListFacetChips controller={controller} />,
};

export default VoterApplicationListFacets;
